import { useState, useEffect, useRef } from 'react';
import {
  useReadContracts,
  useReadContract,
  useWriteContract,
  useWaitForTransactionReceipt,
  useChainId,
  useAccount,
  useBalance,
} from 'wagmi';
import { formatUnits, isAddress, erc20Abi } from 'viem';
import { toast } from 'sonner';
import {
  Layers,
  ExternalLink,
  Copy,
  Check,
  Wallet,
  Settings2,
  Send,
  RefreshCw,
  Plus,
  Coins,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Badge } from '@/shared/ui/badge';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { contracts, galactica, galacticaCassiopeia } from '@/shared/config';
import { indexPoolAbi } from '@/shared/config/abis';
import { Instructions, Step } from '@/shared/ui/instructions';

function CopyAddress({ address }: { address: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(address);
    setCopied(true);
    toast.success('Address copied');
    setTimeout(() => setCopied(false), 2000);
  };

  const truncate = (addr: string) => `${addr.slice(0, 6)}...${addr.slice(-4)}`;

  return (
    <div className="flex items-center gap-2">
      <code className="text-sm font-mono">{truncate(address)}</code>
      <button onClick={copy} className="text-[#807f7e] hover:text-[#2b2b2b]">
        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
}

function StatRow({ label, value, isLoading }: { label: string; value: React.ReactNode; isLoading?: boolean }) {
  return (
    <div className="flex justify-between py-3 border-b border-gray-100 last:border-0">
      <span className="text-[#807f7e]">{label}</span>
      {isLoading ? <Skeleton className="h-5 w-32" /> : <span className="font-medium">{value}</span>}
    </div>
  );
}

interface AdminActionProps {
  title: string;
  description: string;
  buttonText: string;
  onAction: () => void;
  isPending: boolean;
  inputPlaceholder?: string;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  disabled?: boolean;
}

function AdminAction({
  title,
  description,
  buttonText,
  onAction,
  isPending,
  inputPlaceholder,
  inputValue,
  onInputChange,
  disabled,
}: AdminActionProps) {
  return (
    <div className="p-4 border rounded-lg space-y-3">
      <div>
        <h4 className="font-medium">{title}</h4>
        <p className="text-sm text-[#807f7e]">{description}</p>
      </div>
      {inputPlaceholder && onInputChange && (
        <Input
          placeholder={inputPlaceholder}
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          className="font-mono text-sm"
        />
      )}
      <Button
        onClick={onAction}
        disabled={isPending || disabled}
        size="sm"
      >
        {isPending ? (
          <>
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            Processing...
          </>
        ) : (
          buttonText
        )}
      </Button>
    </div>
  );
}

type PoolAsset = { address: string; symbol: string; balance: string; priceUSD: number; valueUSD: number };

function TokenRow({
  token,
  poolAddress,
  explorerUrl,
  priceInfo,
}: {
  token: string;
  poolAddress: string;
  explorerUrl: string;
  priceInfo?: PoolAsset;
}) {
  const { data: balance } = useReadContract({
    address: token as `0x${string}`,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: [poolAddress as `0x${string}`],
  });

  const { data: symbol } = useReadContract({
    address: token as `0x${string}`,
    abi: erc20Abi,
    functionName: 'symbol',
  });

  const { data: decimals } = useReadContract({
    address: token as `0x${string}`,
    abi: erc20Abi,
    functionName: 'decimals',
  });

  const formattedBalance = balance && decimals
    ? Number(formatUnits(balance, decimals)).toLocaleString(undefined, { maximumFractionDigits: 4 })
    : '—';

  const priceUSD = priceInfo?.priceUSD ?? 0;
  const valueUSD = priceInfo?.valueUSD ?? 0;

  return (
    <TableRow>
      <TableCell>
        <a
          href={`${explorerUrl}/address/${token}`}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-sm text-[#f26f56] hover:underline"
        >
          {`${token.slice(0, 6)}...${token.slice(-4)}`}
        </a>
      </TableCell>
      <TableCell>{symbol || '—'}</TableCell>
      <TableCell className="text-right font-mono">{formattedBalance}</TableCell>
      <TableCell className="text-right font-mono">${priceUSD.toFixed(4)}</TableCell>
      <TableCell className="text-right font-mono">${valueUSD.toFixed(2)}</TableCell>
    </TableRow>
  );
}

export function IndexPoolPage() {
  const chainId = useChainId();
  const { address: walletAddress, isConnected } = useAccount();
  const currentChain = chainId === galactica.id ? galactica : galacticaCassiopeia;

  const indexPoolAddress = contracts.indexPool[chainId as keyof typeof contracts.indexPool];

  // Form states
  const [newToken, setNewToken] = useState('');
  const [newOwner, setNewOwner] = useState('');

  // Pool data from API (for pricing)
  const [poolData, setPoolData] = useState<{ composition: PoolAsset[]; totalWorthUSD: number } | null>(null);

  useEffect(() => {
    console.log('[IndexPool] Fetching pool data for chainId:', chainId);
    fetch(`/api/pool?chainId=${chainId}`)
      .then(res => {
        if (!res.ok) throw new Error(`Pool API error: ${res.status}`);
        return res.json();
      })
      .then(data => {
        console.log('[IndexPool] Pool data received:', data);
        if (data.error) throw new Error(data.error);
        setPoolData(data);
      })
      .catch(err => console.error('[IndexPool] Pool fetch error:', err));
  }, [chainId]);

  // Create a map of address -> price info for quick lookup (lowercased for case-insensitive matching)
  const priceMap = new Map<string, PoolAsset>();
  poolData?.composition.forEach(asset => {
    priceMap.set(asset.address.toLowerCase(), asset);
  });

  // Read contract data (auto-refresh every 15 seconds)
  const { data, isLoading, refetch } = useReadContracts({
    contracts: [
      { address: indexPoolAddress, abi: indexPoolAbi, functionName: 'owner' },
      { address: indexPoolAddress, abi: indexPoolAbi, functionName: 'pendingOwner' },
      { address: indexPoolAddress, abi: indexPoolAbi, functionName: 'indexToken' },
      { address: indexPoolAddress, abi: indexPoolAbi, functionName: 'getHeldTokens' },
    ],
    query: {
      refetchInterval: 30000,
      refetchOnWindowFocus: true,
      placeholderData: (prev) => prev,
      staleTime: 30000,
      gcTime: 5 * 60 * 1000,
      retry: 3,
      retryDelay: 1000,
    },
  });

  const [owner, pendingOwner, indexToken, heldTokens] = data || [];

  // Log errors in development
  useEffect(() => {
    if (data) {
      data.forEach((result, i) => {
        if (result?.status === 'failure') {
          console.warn(`Contract call ${i} failed:`, result.error);
        }
      });
    }
  }, [data]);

  // Get native balance of the pool
  const { data: nativeBalance } = useBalance({
    address: indexPoolAddress,
  });

  // Write contract hooks
  const { writeContract, isPending: isWritePending, data: txHash } = useWriteContract();
  const { isLoading: isTxLoading, isSuccess: isTxSuccess } = useWaitForTransactionReceipt({
    hash: txHash,
  });

  // Track which tx we've already handled
  const handledTxRef = useRef<string | null>(null);

  useEffect(() => {
    if (isTxSuccess && txHash && handledTxRef.current !== txHash) {
      handledTxRef.current = txHash;
      toast.success('Transaction confirmed');
      refetch();
    }
  }, [isTxSuccess, txHash, refetch]);

  const isPending = isWritePending || isTxLoading;

  // Check if current wallet is owner
  const isOwner = walletAddress && owner?.result && walletAddress.toLowerCase() === (owner.result as string).toLowerCase();
  const isPendingOwnerWallet = walletAddress && pendingOwner?.result && walletAddress.toLowerCase() === (pendingOwner.result as string).toLowerCase();

  // Admin actions
  const handleAddToken = () => {
    if (!isAddress(newToken)) {
      toast.error('Invalid token address');
      return;
    }
    writeContract({
      address: indexPoolAddress,
      abi: indexPoolAbi,
      functionName: 'addToken',
      args: [newToken as `0x${string}`],
    });
  };

  const handleTransferOwnership = () => {
    if (!isAddress(newOwner)) {
      toast.error('Invalid address');
      return;
    }
    writeContract({
      address: indexPoolAddress,
      abi: indexPoolAbi,
      functionName: 'transferOwnership',
      args: [newOwner as `0x${string}`],
    });
  };

  const handleAcceptOwnership = () => {
    writeContract({
      address: indexPoolAddress,
      abi: indexPoolAbi,
      functionName: 'acceptOwnership',
    });
  };

  const explorerUrl = currentChain.blockExplorers?.default.url || '';
  const tokens = (heldTokens?.result as string[]) || [];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#2b2b2b]">Index Pool</h1>
          <p className="text-[#807f7e]">Manage gUBI backing pool</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <a
            href={`${explorerUrl}/address/${indexPoolAddress}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="outline">
              <ExternalLink className="w-4 h-4 mr-2" />
              Explorer
            </Button>
          </a>
        </div>
      </div>

      {/* Pricing sources documentation */}
      <Instructions title="Token pricing sources">
        <Step number={1} title="WGNET (Wrapped GNET)">
          Price fetched from CoinGecko API using GNET price (galactica-com).
          Endpoint: api.coingecko.com/api/v3/simple/price?ids=galactica-com&vs_currencies=usd
        </Step>
        <Step number={2} title="Adding new tokens">
          When adding new tokens to the pool, update the pricing logic in
          src/worker.ts with the appropriate CoinGecko ID or oracle source.
        </Step>
        <p className="text-xs text-[#807f7e] mt-2">
          Prices are cached in D1 for 5 minutes. If CoinGecko fails, last known price is used.
        </p>
      </Instructions>

      {/* Connection warning */}
      {!isConnected && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Wallet className="w-5 h-5 text-yellow-600" />
              <p className="text-yellow-800">Connect your wallet to use admin functions</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contract Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5" />
              Contract Info
            </CardTitle>
            <CardDescription>Index Pool contract statistics</CardDescription>
          </CardHeader>
          <CardContent>
            <StatRow
              label="Contract Address"
              value={
                <a
                  href={`${explorerUrl}/address/${indexPoolAddress}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#f26f56] hover:underline font-mono text-sm"
                >
                  {indexPoolAddress}
                </a>
              }
            />
            <StatRow
              label="Index Token (gUBI)"
              value={indexToken?.result ? <CopyAddress address={indexToken.result as string} /> : '—'}
              isLoading={isLoading}
            />
            <StatRow
              label="Native Balance"
              value={nativeBalance ? `${Number(formatUnits(nativeBalance.value, nativeBalance.decimals)).toLocaleString()} ${nativeBalance.symbol}` : '—'}
              isLoading={!nativeBalance}
            />
            <StatRow
              label="Held Tokens"
              value={`${tokens.length} tokens`}
              isLoading={isLoading}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5" />
              Ownership
            </CardTitle>
            <CardDescription>Contract ownership configuration</CardDescription>
          </CardHeader>
          <CardContent>
            <StatRow
              label="Owner"
              value={owner?.result ? <CopyAddress address={owner.result as string} /> : '—'}
              isLoading={isLoading}
            />
            <StatRow
              label="Pending Owner"
              value={
                pendingOwner?.result && pendingOwner.result !== '0x0000000000000000000000000000000000000000' ? (
                  <CopyAddress address={pendingOwner.result as string} />
                ) : (
                  <span className="text-[#807f7e]">None</span>
                )
              }
              isLoading={isLoading}
            />
          </CardContent>
        </Card>
      </div>

      {/* Held Tokens */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Coins className="w-5 h-5" />
            Held Tokens
          </CardTitle>
          <CardDescription>Assets backing the gUBI token</CardDescription>
        </CardHeader>
        <CardContent>
          {tokens.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Address</TableHead>
                  <TableHead>Symbol</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tokens.map((token) => (
                  <TokenRow
                    key={token}
                    token={token}
                    poolAddress={indexPoolAddress}
                    explorerUrl={explorerUrl}
                    priceInfo={priceMap.get(token.toLowerCase())}
                  />
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-[#807f7e] text-center py-8">No tokens in the pool</p>
          )}
        </CardContent>
      </Card>

      {/* Admin Actions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                Admin Actions
              </CardTitle>
              <CardDescription>Execute contract admin functions (owner only)</CardDescription>
            </div>
            {isOwner && (
              <Badge variant="secondary" className="bg-green-100 text-green-700">
                You are the owner
              </Badge>
            )}
            {!isOwner && isConnected && (
              <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">
                Not owner
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <AdminAction
            title="Add Token"
            description="Add a new token to the pool's held assets"
            buttonText="Add Token"
            onAction={handleAddToken}
            isPending={isPending}
            inputPlaceholder="Token address (0x...)"
            inputValue={newToken}
            onInputChange={setNewToken}
            disabled={!isOwner}
          />

          <AdminAction
            title="Transfer Ownership"
            description="Transfer contract ownership to a new address (2-step)"
            buttonText="Transfer"
            onAction={handleTransferOwnership}
            isPending={isPending}
            inputPlaceholder="New owner address (0x...)"
            inputValue={newOwner}
            onInputChange={setNewOwner}
            disabled={!isOwner}
          />

          {isPendingOwnerWallet && (
            <AdminAction
              title="Accept Ownership"
              description="You are the pending owner. Accept to become the new owner."
              buttonText="Accept Ownership"
              onAction={handleAcceptOwnership}
              isPending={isPending}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
